'use client';

/**
 * Employer Messages Page
 *
 * Messaging center for employee benefits communications.
 * Connected to the member communications backend API.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  Send,
  Inbox,
  Search,
  Mail,
  Phone,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X,
  Loader2,
} from 'lucide-react';
import EmployerLayout from '../layout';
import { getMemberData, getMemberToken, getCommunications, markCommunicationRead, respondToCommunication, type Communication } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';

export default function EmployerMessagesPage() {
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected message for viewing
  const [selectedMessage, setSelectedMessage] = useState<Communication | null>(null);

  // Reply state
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySuccess, setReplySuccess] = useState(false);

  // Compose modal state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState<'case_manager' | 'organization' | 'employer' | 'support'>('organization');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);

  const member = getMemberData();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Recipient options based on member's relationships
  const recipientOptions = [
    { id: 'case_manager', label: 'My Case Manager', description: 'Your assigned care coordinator' },
    { id: 'organization', label: member?.organization_name || 'My Organization', description: 'CBO or support organization' },
    { id: 'employer', label: 'HR & Benefits Team', description: 'Your employer\'s HR department' },
    { id: 'support', label: 'WeaveIn Support', description: 'Platform help and technical support' },
  ] as const;

  // Load communications
  useEffect(() => {
    loadCommunications();
  }, [member?.id]);

  const loadCommunications = async (preserveReadState = false) => {
    if (!member?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getCommunications(false); // Always fetch all, filter client-side
      if (!mountedRef.current) return;

      if (preserveReadState && communications.length > 0) {
        // Merge with existing read states from local state
        const readIds = new Set(
          communications.filter(c => c.status === 'read').map(c => c.id)
        );
        const mergedData = (data || []).map(comm => ({
          ...comm,
          status: readIds.has(comm.id) ? 'read' : comm.status
        }));
        setCommunications(mergedData);
      } else {
        setCommunications(data || []);
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      console.error('Failed to load communications:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Filter communications
  const filteredCommunications = communications.filter(comm => {
    if (filter === 'unread' && comm.status === 'read') return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        comm.subject?.toLowerCase().includes(query) ||
        comm.message_body?.toLowerCase().includes(query) ||
        comm.sender_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const unreadCount = communications.filter(c => c.status !== 'read').length;

  // Handle marking as read
  const handleMarkRead = async (commId: number) => {
    try {
      await markCommunicationRead(commId);
      setCommunications(prev =>
        prev.map(c =>
          c.id === commId ? { ...c, status: 'read', read_at: new Date().toISOString() } : c
        )
      );
    } catch (err: any) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Handle selecting a message
  const handleSelectMessage = (comm: Communication) => {
    setSelectedMessage(comm);
    setReplyText('');
    setReplyError(null);
    setReplySuccess(false);

    if (comm.status !== 'read') {
      handleMarkRead(comm.id);
    }
  };

  // Handle sending reply
  const handleSendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;

    setReplying(true);
    setReplyError(null);

    try {
      await respondToCommunication(selectedMessage.id, replyText);
      setReplySuccess(true);
      setCommunications(prev =>
        prev.map(c =>
          c.id === selectedMessage.id
            ? { ...c, response_message: replyText, responded_at: new Date().toISOString() }
            : c
        )
      );
      setReplyText('');
      setTimeout(() => setReplySuccess(false), 3000);
    } catch (err: any) {
      setReplyError(err.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  // Handle composing new message
  const handleSendNewMessage = async () => {
    if (!composeSubject.trim() || !composeMessage.trim()) return;

    setComposeSending(true);
    setComposeError(null);

    try {
      const token = getMemberToken();
      if (!token) {
        setComposeError('Not authenticated');
        return;
      }

      const response = await fetch(getWeaveinApiUrl('/api/member-portal/communications/send'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: composeSubject,
          message_body: composeMessage,
          recipient_type: composeRecipient,
          urgency_level: 'normal',
        }),
      });

      if (response.ok) {
        setComposeOpen(false);
        setComposeRecipient('organization');
        setComposeSubject('');
        setComposeMessage('');
        loadCommunications(); // Refresh list
      } else {
        const errorData = await response.json();
        setComposeError(errorData.detail || 'Failed to send message');
      }
    } catch (err: any) {
      setComposeError(err.message || 'Network error');
    } finally {
      setComposeSending(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'sms':
      case 'phone':
        return <Phone className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  return (
    <EmployerLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600 mt-1">Your communications and notifications</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadCommunications(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setComposeOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              New Message
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Toolbar */}
            <div className="p-4 border-b border-gray-200 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'unread', label: `Unread (${unreadCount})` },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id as typeof filter)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      filter === f.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-indigo-600 animate-spin" />
                  <p className="mt-2 text-sm text-gray-500">Loading messages...</p>
                </div>
              ) : filteredCommunications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Inbox className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p>{communications.length === 0 ? 'No messages yet' : 'No messages found'}</p>
                </div>
              ) : (
                filteredCommunications.map((comm) => (
                  <div
                    key={comm.id}
                    onClick={() => handleSelectMessage(comm)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      comm.status !== 'read' ? 'bg-indigo-50/50' : ''
                    } ${selectedMessage?.id === comm.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        comm.communication_type === 'email' ? 'bg-blue-100' : 'bg-green-100'
                      }`}>
                        {getTypeIcon(comm.communication_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${comm.status !== 'read' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {comm.sender_name || 'System'}
                          </span>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatDate(comm.created_at)}
                          </span>
                        </div>
                        <h3 className={`text-sm truncate ${comm.status !== 'read' ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>
                          {comm.subject || 'No subject'}
                        </h3>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {comm.message_body?.substring(0, 60)}...
                        </p>
                      </div>
                      {comm.status !== 'read' && (
                        <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
            {selectedMessage ? (
              <div className="h-full flex flex-col">
                {/* Message Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedMessage.communication_type === 'email' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      {getTypeIcon(selectedMessage.communication_type)}
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">{selectedMessage.sender_name || 'System'}</h2>
                      <p className="text-sm text-gray-500">{formatDate(selectedMessage.created_at)}</p>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedMessage.subject || 'No subject'}</h3>
                </div>

                {/* Message Body */}
                <div className="flex-1 p-4 overflow-y-auto">
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                    {selectedMessage.message_body}
                  </div>

                  {/* Previous Response */}
                  {selectedMessage.response_message && (
                    <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Your Response</span>
                        <span className="text-xs text-green-600">
                          {selectedMessage.responded_at && formatDate(selectedMessage.responded_at)}
                        </span>
                      </div>
                      <p className="text-sm text-green-800">{selectedMessage.response_message}</p>
                    </div>
                  )}
                </div>

                {/* Reply Section */}
                <div className="p-4 border-t border-gray-200">
                  {replySuccess && (
                    <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Reply sent successfully!
                    </div>
                  )}
                  {replyError && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {replyError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none text-sm"
                      rows={2}
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={replying || !replyText.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500 p-8">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a message</h3>
                  <p className="text-sm">Choose a message from the list to view its contents</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Compose Modal */}
        {composeOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">New Message</h3>
                <button
                  onClick={() => setComposeOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                {composeError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {composeError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <div className="space-y-2">
                    {recipientOptions.map((option) => (
                      <label
                        key={option.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          composeRecipient === option.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="recipient"
                          value={option.id}
                          checked={composeRecipient === option.id}
                          onChange={(e) => setComposeRecipient(e.target.value as typeof composeRecipient)}
                          className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{option.label}</div>
                          <div className="text-xs text-gray-500">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Enter subject..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={composeMessage}
                    onChange={(e) => setComposeMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    rows={5}
                  />
                </div>
              </div>
              <div className="flex gap-3 p-4 border-t border-gray-200">
                <button
                  onClick={() => setComposeOpen(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNewMessage}
                  disabled={composeSending || !composeSubject.trim() || !composeMessage.trim()}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {composeSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </EmployerLayout>
  );
}
